import { useCallback, useState } from "react";
import { RotateCcw } from "lucide-react-native";
import { Platform, Text, View, type ImageSourcePropType } from "react-native";
import {
  ActionBanner,
  ClinicalDataStatus,
  CompactActionButton,
} from "@louise/moon-ui";
import {
  useAppleHealthImportStatus,
  useAppleHealthPeriodSync,
} from "@src/packages/health";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { GroupedCard } from "../components";
import StatusBadge from "../../../components/StatusBadge";
import CalendarSyncIntegration from "./CalendarSyncIntegration";
import { IntegrationRow } from "./IntegrationRow";

type PlannedIntegrationId = "oura" | "tempdrop" | "natural-cycles";

type PlannedIntegration = {
  id: PlannedIntegrationId;
  name: string;
  descriptionKey: string;
  logo: ImageSourcePropType;
};

const APPLE_HEALTH_LOGO =
  require("../../../../assets/images/integrations/apple-health.png") as ImageSourcePropType;

const PLANNED_INTEGRATIONS: PlannedIntegration[] = [
  {
    id: "oura",
    name: "Oura Ring",
    descriptionKey: "you.integrations.oura_description",
    logo: require("../../../../assets/images/integrations/oura.png") as ImageSourcePropType,
  },
  {
    id: "tempdrop",
    name: "Tempdrop",
    descriptionKey: "you.integrations.tempdrop_description",
    logo: require("../../../../assets/images/integrations/tempdrop.png") as ImageSourcePropType,
  },
  {
    id: "natural-cycles",
    name: "Natural Cycles",
    descriptionKey: "you.integrations.natural_cycles_description",
    // The legacy natural-cycles.png is a transparent 1×1 placeholder. Use
    // the established dimensional moon cue until an approved brand asset is
    // available, rather than rendering an unexplained empty leading slot.
    logo: require("../../../../assets/images/you-icons/moon.png") as ImageSourcePropType,
  },
];

/**
 * Apple Health and calendar sync are the live integrations today. The
 * remaining sources stay visible as product availability, but do not expose
 * fake connect/sync controls before a secure connector and durable backend
 * state exist.
 */
export default function Integrations() {
  const t = useTranslations();
  const appleHealthSync = useAppleHealthPeriodSync();
  const {
    data: importedAppleHealthData,
    isError: appleHealthStatusLoadFailed,
    isFetching: isFetchingAppleHealthStatus,
    refetch: refetchAppleHealthStatus,
  } = useAppleHealthImportStatus();
  const [importedThisSession, setImportedThisSession] = useState(false);
  const [noCompatibleDataFound, setNoCompatibleDataFound] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const isAppleHealthAvailable = Platform.OS === "ios";
  const hasAppleHealthData =
    Boolean(importedAppleHealthData) || importedThisSession;

  const syncAppleHealth = useCallback(async () => {
    setSyncFailed(false);
    setNoCompatibleDataFound(false);

    try {
      const result = await appleHealthSync.mutateAsync();
      const importedAnyData =
        result.imported > 0 || result.observationsImported > 0;
      const importWasIncomplete =
        result.failed > 0 || result.observationsFailed > 0;

      setImportedThisSession(importedAnyData);
      setNoCompatibleDataFound(!importedAnyData && !importWasIncomplete);
      setSyncFailed(importWasIncomplete);
    } catch {
      setSyncFailed(true);
    }
  }, [appleHealthSync]);

  return (
    <View>
      <GroupedCard>
        <CalendarSyncIntegration />

        <IntegrationRow
          source={APPLE_HEALTH_LOGO}
          title="Apple Health"
          divider
          subtitle={t(
            hasAppleHealthData
              ? "you.integrations.apple_health_data_on_file"
              : noCompatibleDataFound
                ? "you.integrations.apple_health_no_compatible_data"
                : "you.integrations.apple_health_description",
          )}
          trailing={
            isAppleHealthAvailable ? (
              <CompactActionButton
                label={t(
                  hasAppleHealthData
                    ? "you.integrations.refresh_data"
                    : "you.integrations.import_data",
                )}
                size="sm"
                variant="primary"
                loading={appleHealthSync.isPending}
                onPress={syncAppleHealth}
              />
            ) : (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: EMBER.colors.textFaint,
                  fontWeight: "500",
                }}
              >
                {t("you.integrations.iphone_only")}
              </Text>
            )
          }
        />

        {PLANNED_INTEGRATIONS.map((integration) => (
          <IntegrationRow
            key={integration.id}
            source={integration.logo}
            title={integration.name}
            subtitle={t(integration.descriptionKey)}
            divider
            trailing={<StatusBadge type="coming-soon" />}
          />
        ))}
      </GroupedCard>

      {appleHealthStatusLoadFailed ? (
        <ClinicalDataStatus
          state="stale"
          message={t("you.integrations.apple_health_status_load_failed")}
          retryLabel={t("clinical_data.retry")}
          retryingLabel={t("clinical_data.retrying")}
          onRetry={() => void refetchAppleHealthStatus()}
          isRetrying={isFetchingAppleHealthStatus}
          style={{ marginTop: 12 }}
          testID="apple-health-status-stale"
        />
      ) : null}

      {syncFailed ? (
        <View style={{ marginTop: 12 }}>
          <ActionBanner
            variant="important"
            message={t("you.integrations.apple_health_import_failed")}
            actionLabel={t("you.integrations.try_again")}
            actionIcon={RotateCcw}
            onAction={() => void syncAppleHealth()}
          />
        </View>
      ) : null}
    </View>
  );
}
